import React, { useEffect, useRef, useState } from 'react';
import './Marketing.css';
import heroDesktop from './desktop.png';
import { useTranslation } from "react-i18next";
import { FaArrowUp } from 'react-icons/fa';
import logoImage from './logo.png';

const Marketing = () => {
  const { t, i18n } = useTranslation("marketing");
  const supported = (i18n.options?.supportedLngs || ['en', 'es']).filter(l => l !== 'cimode');
  const [lang, setLang] = useState(i18n.language);
  const [showScrollTop, setShowScrollTop] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const pageRef = useRef(null);
  const progressRef = useRef(null);

  useEffect(() => {
    const prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

    const revealEls = document.querySelectorAll('.mkp-reveal');
    let observer;

    if (!prefersReduced && 'IntersectionObserver' in window) {
      observer = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting) {
              entry.target.classList.add('mkp-reveal-visible');
              observer.unobserve(entry.target);
            }
          });
        },
        { threshold: 0.12, rootMargin: '0px 0px -64px 0px' }
      );
      revealEls.forEach((el) => observer.observe(el));
    } else {
      revealEls.forEach((el) => el.classList.add('mkp-reveal-visible'));
    }

    let ticking = false;
    const update = () => {
      const y = window.scrollY || window.pageYOffset || 0;
      setScrolled(y > 24);
      setShowScrollTop(y > 480);

      const doc = document.documentElement;
      const max = doc.scrollHeight - doc.clientHeight || 1;
      const progress = Math.min(Math.max(y / max, 0), 1);
      if (progressRef.current) {
        progressRef.current.style.transform = `scaleX(${progress})`;
      }
      if (!prefersReduced && pageRef.current) {
        pageRef.current.style.setProperty('--mkp-sy', String(y));
      }
      ticking = false;
    };

    const onScroll = () => {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    };

    window.addEventListener('scroll', onScroll, { passive: true });
    update();

    return () => {
      if (observer) observer.disconnect();
      window.removeEventListener('scroll', onScroll);
    };
  }, []);

  const scrollToTop = () => {
    const prefersReduced =
      window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    window.scrollTo({ top: 0, behavior: prefersReduced ? 'auto' : 'smooth' });
  };

  const pricingTiers = [
    {
      name: t("pricing.tiers.starter.name"),
      price: t("pricing.tiers.starter.price"),
      period: t("pricing.tiers.starter.period"),
      description: t("pricing.tiers.starter.description"),
      badge: t("pricing.tiers.starter.badge"),
      features: [
        { text: t("pricing.tiers.starter.features.trial"), included: true },
        { text: t("pricing.tiers.starter.features.staff"), included: true },
        { text: t("pricing.tiers.starter.features.services"), included: true },
        { text: t("pricing.tiers.starter.features.miniSite"), included: false },
        { text: t("pricing.tiers.starter.features.onlinePayments"), included: false }
      ]
    },
    {
      name: t("pricing.tiers.growth.name"),
      price: t("pricing.tiers.growth.price"),
      period: t("pricing.tiers.growth.period"),
      description: t("pricing.tiers.growth.description"),
      badge: t("pricing.tiers.growth.badge"),
      features: [
        { text: t("pricing.tiers.growth.features.trial"), included: true },
        { text: t("pricing.tiers.growth.features.staff"), included: true },
        { text: t("pricing.tiers.growth.features.services"), included: true },
        { text: t("pricing.tiers.growth.features.miniSite"), included: true },
        { text: t("pricing.tiers.growth.features.onlinePayments"), included: true }
      ]
    },
    {
      name: t("pricing.tiers.pro.name"),
      price: t("pricing.tiers.pro.price"),
      period: t("pricing.tiers.pro.period"),
      description: t("pricing.tiers.pro.description"),
      badge: t("pricing.tiers.pro.badge"),
      features: [
        { text: t("pricing.tiers.pro.features.trial"), included: true },
        { text: t("pricing.tiers.pro.features.staff"), included: true },
        { text: t("pricing.tiers.pro.features.services"), included: true },
        { text: t("pricing.tiers.pro.features.miniSite"), included: true },
        { text: t("pricing.tiers.pro.features.onlinePayments"), included: true }
      ]
    },
    {
      name: t("pricing.tiers.elite.name"),
      price: t("pricing.tiers.elite.price"),
      period: t("pricing.tiers.elite.period"),
      description: t("pricing.tiers.elite.description"),
      badge: t("pricing.tiers.elite.badge"),
      features: [
        { text: t("pricing.tiers.elite.features.trial"), included: true },
        { text: t("pricing.tiers.elite.features.staff"), included: true },
        { text: t("pricing.tiers.elite.features.services"), included: true },
        { text: t("pricing.tiers.elite.features.miniSite"), included: true },
        { text: t("pricing.tiers.elite.features.onlinePayments"), included: true }
      ]
    }
  ];

  const coreFeatures = [
    {
      title: t("features.cards.dayAtAGlance.title"),
      text: t("features.cards.dayAtAGlance.text"),
      icon: '📊'
    },
    {
      title: t("features.cards.manageServices.title"),
      text: t("features.cards.manageServices.text"),
      icon: '🧾'
    },
    {
      title: t("features.cards.staffSchedules.title"),
      text: t("features.cards.staffSchedules.text"),
      icon: '🗓️'
    },
    {
      title: t("features.cards.keepAppointments.title"),
      text: t("features.cards.keepAppointments.text"),
      icon: '📅'
    },
    {
      title: t("features.cards.customerExperience.title"),
      text: t("features.cards.customerExperience.text"),
      icon: '💬'
    },
    {
      title: t("features.cards.reduceNoShows.title"),
      text: t("features.cards.reduceNoShows.text"),
      icon: '💳'
    }
  ];

  const businessBenefits = [
    t("benefits.items.noShows"),
    t("benefits.items.adminTime"),
    t("benefits.items.staffSchedules"),
    t("benefits.items.centralPlace"),
    t("benefits.items.professionalExperience"),
    t("benefits.items.lessStress")
  ];

  return (
    <div className="mkp-page" ref={pageRef}>
      <div className="mkp-progress" aria-hidden="true">
        <i ref={progressRef} />
      </div>

      <header className={`mkp-header ${scrolled ? 'mkp-header--scrolled' : ''}`}>
        <div className="mkp-container">
          <div className="mkp-nav">
            <div className="mkp-logo">
              <img src={logoImage} alt={process.env.REACT_APP_COMPANY_NAME} />
              <span className='mkp-site-title'>{process.env.REACT_APP_COMPANY_NAME}</span>
            </div>

            <nav className="mkp-nav-links">
              <a href="#features">{t("nav.links.features")}</a>
              <a href="#benefits">{t("nav.links.benefits")}</a>
              <a href="#pricing">{t("nav.links.pricing")}</a>
              <a href="#faq">{t("nav.links.faq")}</a>
            </nav>

            <div className="mkp-nav-actions">
              <label htmlFor="lang-switch" className="visually-hidden">
                {t('login.lang.ariaLabel')}
              </label>

              <select
                id="lang-switch"
                value={lang}
                onChange={(e) => {
                  i18n.changeLanguage(e.target.value);
                  setLang(e.target.value);
                  localStorage.setItem('ui.lang', e.target.value);
                }}
                aria-label={t('login.lang.ariaLabel')}
                className="mkp-lang-select"
              >
                {supported.map(code => (
                  <option key={code} value={code}>
                    {t(`login.lang.names.${code}`, code.toUpperCase())}
                  </option>
                ))}
              </select>

              <a target="_blank" rel="noreferrer" className="mkp-btn mkp-btn-outline" href="/login">
                {t("nav.login")}
              </a>
            </div>
          </div>
        </div>
      </header>

      <main>
        <section className="mkp-hero-section">
          <div className="mkp-container mkp-hero-grid">
            <div className="mkp-hero-content mkp-reveal">
              <div className="mkp-hero-pill">{t("hero.pill")}</div>

              <h1>
                <span className="mkp-hero-line">{t("hero.title.line1")}</span>
                <span>{t("hero.title.line2")}</span>
              </h1>

              <p className="mkp-hero-text">
                {t("hero.text")}
              </p>

              <div className="mkp-hero-actions">
                <a target="_blank" rel="noreferrer" className="mkp-btn mkp-btn-primary" href="/register">
                  {t("hero.actions.createAccount")}
                </a>

                {/* <a href="#pricing" className="mkp-btn mkp-btn-secondary">
                  {t("hero.actions.viewPricing")}
                </a> */}

                <a
                  className="mkp-btn mkp-btn-secondary"
                  href="tel:07825597447"
                >
                  {t("hero.demoTitle")}
                </a>
              </div>

              <p className="mkp-demo-note">
                {t(
                  "hero.demoText"
                )}
              </p>

              <div className="mkp-hero-trust">
                <div>✔ {t("hero.trust.one")}</div>
                <div>✔ {t("hero.trust.two")}</div>
                <div>✔ {t("hero.trust.three")}</div>
              </div>
            </div>

            <div className="mkp-hero-visual mkp-reveal mkp-reveal-delay-1">
              <div className="mkp-hero-glow"></div>

              <div className="mkp-desktop-frame">
                <div className="mkp-browser-bar">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>

                <img src={heroDesktop} alt={t("hero.imageAlt")} />
              </div>

              <div className="mkp-hero-badge" aria-hidden="true">
                <span className="mkp-hero-badge__num">{t("hero.badge.value")}</span>
                <span className="mkp-hero-badge__label">{t("hero.badge.label")}</span>
              </div>
            </div>
          </div>
        </section>

        <section className="mkp-stats-section">
          <div className="mkp-container mkp-stats-grid">
            <div className="mkp-stat-card mkp-reveal">
              <h3>{t("stats.centralSystem.title")}</h3>
              <p>{t("stats.centralSystem.text")}</p>
            </div>

            <div className="mkp-stat-card mkp-reveal mkp-reveal-delay-1">
              <h3>{t("stats.lessAdmin.title")}</h3>
              <p>{t("stats.lessAdmin.text")}</p>
            </div>

            <div className="mkp-stat-card mkp-reveal mkp-reveal-delay-2">
              <h3>{t("stats.professionalExperience.title")}</h3>
              <p>{t("stats.professionalExperience.text")}</p>
            </div>
          </div>
        </section>

        <section id="features" className="mkp-features-section">
          <div className="mkp-container">
            <div className="mkp-section-heading mkp-section-heading-center mkp-reveal">
              <span className="mkp-section-kicker">{t("features.kicker")}</span>
              <h2>{t("features.title")}</h2>
              <p>{t("features.text")}</p>
            </div>

            <div className="mkp-features-grid">
              {coreFeatures.map((feature, index) => (
                <div className={`mkp-feature-card mkp-reveal mkp-reveal-delay-${index % 3}`} key={index}>
                  <div className="mkp-feature-icon">{feature.icon}</div>
                  <h3>{feature.title}</h3>
                  <p>{feature.text}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mkp-workflow-section">
          <div className="mkp-container mkp-workflow-grid">
            <div className="mkp-workflow-text mkp-reveal">
              <span className="mkp-section-kicker">{t("workflow.kicker")}</span>
              <h2>{t("workflow.title")}</h2>
              <p>{t("workflow.text1")}</p>
              <p>{t("workflow.text2")}</p>

              <div className="mkp-workflow-steps">
                <div className="mkp-workflow-step mkp-reveal">
                  <span>1</span>
                  <div>
                    <h4>{t("workflow.steps.one.title")}</h4>
                    <p>{t("workflow.steps.one.text")}</p>
                  </div>
                </div>

                <div className="mkp-workflow-step mkp-reveal mkp-reveal-delay-1">
                  <span>2</span>
                  <div>
                    <h4>{t("workflow.steps.two.title")}</h4>
                    <p>{t("workflow.steps.two.text")}</p>
                  </div>
                </div>

                <div className="mkp-workflow-step mkp-reveal mkp-reveal-delay-2">
                  <span>3</span>
                  <div>
                    <h4>{t("workflow.steps.three.title")}</h4>
                    <p>{t("workflow.steps.three.text")}</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="mkp-workflow-panel mkp-reveal mkp-reveal-delay-1">
              <div className="mkp-panel-card">
                <h3>{t("workflow.panel.title")}</h3>
                <ul>
                  <li>{t("workflow.panel.items.dashboard")}</li>
                  <li>{t("workflow.panel.items.calendars")}</li>
                  <li>{t("workflow.panel.items.breaks")}</li>
                  <li>{t("workflow.panel.items.leave")}</li>
                  <li>{t("workflow.panel.items.customers")}</li>
                  <li>{t("workflow.panel.items.communications")}</li>
                  <li>{t("workflow.panel.items.payments")}</li>
                  <li>{t("workflow.panel.items.portal")}</li>
                  <li>{t("workflow.panel.items.miniSite")}</li>
                </ul>
              </div>
            </div>
          </div>
        </section>

        <section id="benefits" className="mkp-benefits-section">
          <div className="mkp-container">
            <div className="mkp-section-heading mkp-section-heading-center mkp-reveal">
              <span className="mkp-section-kicker">{t("benefits.kicker")}</span>
              <h2>{t("benefits.title")}</h2>
              <p>{t("benefits.text")}</p>
            </div>

            <div className="mkp-benefits-grid">
              {businessBenefits.map((item, index) => (
                <div className={`mkp-benefit-item mkp-reveal mkp-reveal-delay-${index % 2}`} key={index}>
                  <span className="mkp-benefit-check">✓</span>
                  <p>{item}</p>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mkp-mini-site-section">
          <div className="mkp-container mkp-mini-site-grid">
            <div className="mkp-mini-site-card mkp-reveal">
              <span className="mkp-section-kicker">{t("miniSite.kicker")}</span>
              <h2>{t("miniSite.title")}</h2>
              <p>{t("miniSite.text1")}</p>
              <p>{t("miniSite.text2")}</p>
            </div>

            <div className="mkp-mini-site-preview mkp-reveal mkp-reveal-delay-1">
              <div className="mkp-mini-browser">
                <div className="mkp-mini-sidebar" aria-hidden="true"></div>
                <div className="mkp-dashboard-top">
                  <span className="mkp-dot mkp-dot-pink"></span>
                  <span className="mkp-dot mkp-dot-yellow"></span>
                  <span className="mkp-dot mkp-dot-green"></span>
                </div>
                <div className="mkp-mini-hero"></div>
                <div className="mkp-mini-lines"></div>
                <div className="mkp-mini-service-grid">
                  <div></div>
                  <div></div>
                  <div></div>
                </div>
                <div className="mkp-mini-staff-row"></div>
              </div>
            </div>
          </div>
        </section>

        <section id="pricing" className="mkp-pricing-section">
          <div className="mkp-container">
            <div className="mkp-section-heading mkp-section-heading-center mkp-reveal">
              <span className="mkp-section-kicker">{t("pricing.kicker")}</span>
              <h2>{t("pricing.title")}</h2>
              <p>{t("pricing.text")}</p>
            </div>

            <div className="mkp-pricing-grid">
              {pricingTiers.map((tier, index) => (
                <div
                  className={`mkp-pricing-card ${tier.badge ? 'mkp-pricing-card-featured' : ''} mkp-reveal mkp-reveal-delay-${index % 4}`}
                  key={index}
                >
                  {tier.badge && <div className="mkp-pricing-badge">{tier.badge}</div>}
                  <h3>{tier.name}</h3>

                  <div className="mkp-price-row">
                    <span className="mkp-price">{tier.price}</span>
                    <span className="mkp-period">{tier.period}</span>
                  </div>
                  <p className="mkp-pricing-description">{tier.description}</p>

                  <ul className="mkp-pricing-features">
                    {tier.features.map((feature, featureIndex) => (
                      <li
                        key={featureIndex}
                        className={!feature.included ? 'mkp-pricing-feature-disabled' : ''}
                      >
                        {feature.text}
                      </li>
                    ))}
                  </ul>

                  <a target="_blank" rel="noreferrer" className={`mkp-btn ${tier.badge ? 'mkp-btn-primary' : 'mkp-btn-outline-dark'} mkp-pricing-btn`} href="/register">
                    {t("pricing.cta")}
                  </a>
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="mkp-testimonial-section">
          <div className="mkp-container">
            <div className="mkp-testimonial-card mkp-reveal">
              <p>{t("testimonial.quote")}</p>
              <span>{t("testimonial.author")}</span>
            </div>
          </div>
        </section>

        <section id="faq" className="mkp-faq-section">
          <div className="mkp-container">
            <div className="mkp-section-heading mkp-section-heading-center mkp-reveal">
              <span className="mkp-section-kicker">{t("faq.kicker")}</span>
              <h2>{t("faq.title")}</h2>
            </div>

            <div className="mkp-faq-grid">
              <div className="mkp-faq-card mkp-reveal">
                <h3>{t("faq.items.tryBeforePaying.title")}</h3>
                <p>{t("faq.items.tryBeforePaying.text")}</p>
              </div>

              <div className="mkp-faq-card mkp-reveal mkp-reveal-delay-1">
                <h3>{t("faq.items.takePayments.title")}</h3>
                <p>{t("faq.items.takePayments.text")}</p>
              </div>

              <div className="mkp-faq-card mkp-reveal">
                <h3>{t("faq.items.hasWebsite.title")}</h3>
                <p>{t("faq.items.hasWebsite.text")}</p>
              </div>

              <div className="mkp-faq-card mkp-reveal mkp-reveal-delay-1">
                <h3>{t("faq.items.manageLeave.title")}</h3>
                <p>{t("faq.items.manageLeave.text")}</p>
              </div>
            </div>
          </div>
        </section>

        <section className="mkp-final-cta-section">
          <div className="mkp-container">
            <div className="mkp-final-cta-card mkp-reveal">
              <span className="mkp-section-kicker">{t("finalCta.kicker")}</span>
              <h2>{t("finalCta.title")}</h2>
              <p>{t("finalCta.text")}</p>

              <div style={{margin:"0 auto 15px", maxWidth:"720px"}}>
                <small>{t("hero.demoText")}</small>
              </div>
              <div className="mkp-final-cta-actions">
                {/* <a target="_blank" rel="noreferrer" className="mkp-btn mkp-btn-primary" href="/register">
                  {t("finalCta.actions.createAccount")}
                </a> */}

                <a className="mkp-btn mkp-btn-primary" href="tel:07825597447">
                  {t("hero.demoTitle")}
                </a>

                <a href="#pricing" className="mkp-btn mkp-btn-outline-dark">
                  {t("finalCta.actions.comparePlans")}
                </a>


              </div>
              <small>{t("finalCta.small")}</small>
            </div>
          </div>
        </section>

        <footer className="mkp-footer">
          <div className="mkp-container">
            <div className="mkp-footer-grid">
              <div className="mkp-footer-brand">
                <div className="mkp-logo">
                  <span className="mkp-logo-badge">✦</span>
                  <span>{t("footer.brand")}</span>
                </div>

                <p className="mkp-footer-tagline">
                  {t("footer.tagline")}
                </p>
              </div>
            </div>

            <div className="mkp-footer-bottom">
              <span>{t("footer.bottom.copyright", { year: new Date().getFullYear() })}</span>
              <span>{t("footer.bottom.rights")}</span>
            </div>
          </div>
        </footer>
      </main>

      <button
        type="button"
        className={`mkp-scroll-top ${showScrollTop ? 'mkp-scroll-top--visible' : ''}`}
        onClick={scrollToTop}
        aria-label={t("scrollTop", "Scroll to top")}
      >
        <FaArrowUp />
      </button>
    </div>
  );
};

export default Marketing;